/* eslint-disable class-methods-use-this */
import { EventBus, Events } from "jiffy-store";
import "./product-color-palette.scss";

class ProductColorPalette {
  #colorItemClassName = ".js-product-color-palette-color-item";

  #$root = null;

  #$colorItemActive = null;

  #$expandableContent = null;

  #$expandTrigger = null;

  #$lowStockBadge = null;

  #$materials = null;

  #$oosBadge = null;

  get #contentId() {
    const id = this.#$expandTrigger?.getAttribute("aria-controls");
    return `#${id}`;
  }

  constructor() {
    this.#$root = document.querySelector(".js-product-color-palette");
    this.#$colorItemActive = this.#$root?.querySelector(".js-color-variant-selector-active");
    this.#$expandTrigger = this.#$root?.querySelector(".js-product-color-palette-expand-trigger");
    this.#$expandableContent = this.#$root?.querySelector(this.#contentId);
    this.#$lowStockBadge = this.#$root?.querySelector(".js-color-palette-low-stock-badge");
    this.#$materials = this.#$root?.querySelector(".js-color-palette-materials");
    this.#$oosBadge = this.#$root?.querySelector(".js-color-palette-oos-badge");
  }

  #checkOverflow = () => {
    const hasOverflow =
      this.#$expandableContent.scrollHeight > this.#$expandableContent.clientHeight;
    this.#$expandTrigger.classList.toggle("hidden", !hasOverflow);
  };

  #isActiveElementVisible = () => {
    // Get the container's scroll position and dimensions
    const containerRect = this.#$expandableContent.getBoundingClientRect();
    const containerTop = this.#$expandableContent.scrollTop;
    const containerBottom = containerTop + this.#$expandableContent.clientHeight;

    // Get the element's position relative to the container
    const elementRect = this.#$colorItemActive.getBoundingClientRect();
    const elementTop = elementRect.top - containerRect.top + this.#$expandableContent.scrollTop;
    const elementBottom = elementTop + elementRect.height;

    return elementTop >= containerTop && elementBottom <= containerBottom;
  };

  #isExpanded = () => this.#$expandTrigger.getAttribute("aria-expanded") === "true";

  #getMaterials = materialsRaw => {
    let materials = [];
    try {
      materials = JSON.parse(materialsRaw);
    } catch (error) {
      window.Honeybadger?.notify(`[JS] Failed to parse materials JSON: ${error.message}`, {
        context: { materialsRaw }
      });
    }

    return materials.map(material => {
      const $material = document.createElement("div");
      $material.className = "product-color-palette__material";
      $material.textContent = material;
      return $material;
    });
  };

  #$getColorItemByCompositionId = compositionId =>
    this.#$root.querySelector(
      `${this.#colorItemClassName}[data-composition-id="${compositionId}"]`
    );

  #handleExpandTriggerClick = () => {
    const isExpanded = this.#isExpanded();

    this.#$expandTrigger.setAttribute("aria-expanded", !isExpanded);
    this.#$expandableContent.classList.toggle(
      "product-color-palette__content--expanded",
      !isExpanded
    );
  };

  #handleColorChange = ({ target: $target }) => {
    const $colorItem =
      $target.tagName === "A" ? $target : $target.closest(this.#colorItemClassName);

    const { lowStock, materials: materialsRaw, oos } = $colorItem.dataset;
    const materials = this.#getMaterials(materialsRaw);

    this.#$lowStockBadge.classList.toggle("hidden", lowStock !== "true");
    this.#$materials.replaceChildren(...materials);
    this.#$oosBadge.classList.toggle("hidden", oos !== "true");
  };

  #initEventListeners() {
    this.#$expandTrigger.addEventListener("click", () => {
      this.#handleExpandTriggerClick();
    });
    EventBus.on(Events.PRODUCT_COLOR_SELECTED, (event, { compositionId }) => {
      const $colorItem = this.#$getColorItemByCompositionId(compositionId);
      this.#handleColorChange({ target: $colorItem });
    });
    window.addEventListener("resize", () => {
      if (this.#isExpanded()) return;
      this.#checkOverflow();
    });
  }

  init() {
    if (!this.#$root) return;

    this.#checkOverflow();
    this.#initEventListeners();

    // expand the content if enough space is available
    if (window.innerHeight >= 1000) {
      this.#handleExpandTriggerClick();
    }
    // check if the active element is visible and expand collapsible content if not
    !this.#isActiveElementVisible() && this.#handleExpandTriggerClick();
  }
}

export default () => {
  const productColorPalette = new ProductColorPalette();
  productColorPalette.init();
};
